import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import toast from 'react-hot-toast'
import { FaPhone, FaEnvelope, FaPaperPlane } from 'react-icons/fa'
import apiService from '../../services/apiService'

const contactSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters'),
  email: z.string().email('Invalid email address'),
  phone: z.string().min(10, 'Phone number must be at least 10 digits'),
  subject: z.string().min(3, 'Subject must be at least 3 characters'),
  message: z.string().min(5, 'Message must be at least 5 characters'),
})

const ContactSection = () => {
  const [isSubmitting, setIsSubmitting] = useState(false)

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(contactSchema),
  })

  const onSubmit = async (data) => {
    setIsSubmitting(true)

    try {
      // Direct SMTP email dispatch & Database lead persistence
      await apiService.submitLead({
        name: data.name,
        email: data.email,
        phone: data.phone,
        subject: data.subject,
        message: data.message,
        source: 'Home Contact Section'
      })

      toast.success('Message sent successfully! Our team will contact you shortly.')
      reset()
    } catch (error) {
      console.error('Error sending message:', error)
      toast.error('Failed to send message. Please try again or WhatsApp us directly.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <section id="contact" className="py-20 bg-gray-50">
      <div className="container-custom">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-heading font-extrabold text-secondary-900 mb-4">
            Get in Touch
          </h2>
          <p className="text-secondary-600 text-lg max-w-2xl mx-auto">
            Have questions about our properties or need help finding the right PG? We're here to help you out!
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-8 lg:gap-12 max-w-6xl mx-auto">
          {/* Contact Info Cards */}
          <div className="lg:col-span-1 space-y-4 lg:space-y-6">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
              className="bg-white p-4 sm:p-6 rounded-2xl shadow-soft border border-gray-100 flex items-start gap-3 sm:gap-4"
            >
              <div className="bg-primary-50 p-3 sm:p-4 rounded-xl text-primary-600 shrink-0">
                <FaPhone className="text-lg sm:text-xl" />
              </div>
              <div>
                <h4 className="font-heading font-bold text-secondary-900 mb-1 text-sm sm:text-base">Call Us</h4>
                <p className="text-secondary-600 text-xs sm:text-sm">Mon-Sat from 9am to 6pm</p>
                <a href="tel:+919960697118" className="text-primary-600 font-semibold mt-1 sm:mt-2 inline-block text-sm">+91 99606 97118</a>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
              className="bg-white p-4 sm:p-6 rounded-2xl shadow-soft border border-gray-100 flex items-start gap-3 sm:gap-4"
            >
              <div className="bg-accent-50 p-3 sm:p-4 rounded-xl text-accent-600 shrink-0">
                <FaEnvelope className="text-lg sm:text-xl" />
              </div>
              <div>
                <h4 className="font-heading font-bold text-secondary-900 mb-1 text-sm sm:text-base">Email Us</h4>
                <p className="text-secondary-600 text-xs sm:text-sm">We'll reply within 24 hours</p>
                <a href="mailto:info@chapter24.co.in" className="text-primary-600 font-semibold mt-1 sm:mt-2 inline-block text-sm">info@chapter24.co.in</a>
              </div>
            </motion.div>
          </div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
            className="lg:col-span-2 bg-white rounded-3xl shadow-soft p-6 sm:p-8 md:p-10 border border-gray-100"
          >
            <h3 className="text-xl sm:text-2xl font-heading font-bold text-secondary-900 mb-6 sm:mb-8">Send us a message</h3>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 sm:space-y-6">
              <div className="grid md:grid-cols-2 gap-4 sm:gap-6">
                <div>
                  <label className="block text-xs sm:text-sm font-semibold text-secondary-700 mb-2">Full Name</label>
                  <input
                    type="text"
                    {...register('name')}
                    className="input-field text-sm"
                    placeholder="Full Name"
                  />
                  {errors.name && <p className="text-primary-500 text-xs sm:text-sm mt-1">{errors.name.message}</p>}
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-semibold text-secondary-700 mb-2">Email Address</label>
                  <input
                    type="email"
                    {...register('email')}
                    className="input-field text-sm"
                    placeholder="Email Address"
                  />
                  {errors.email && <p className="text-primary-500 text-xs sm:text-sm mt-1">{errors.email.message}</p>}
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4 sm:gap-6">
                <div>
                  <label className="block text-xs sm:text-sm font-semibold text-secondary-700 mb-2">Phone Number</label>
                  <input
                    type="tel"
                    {...register('phone')}
                    className="input-field text-sm"
                    placeholder="10-digit number"
                  />
                  {errors.phone && <p className="text-primary-500 text-xs sm:text-sm mt-1">{errors.phone.message}</p>}
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-semibold text-secondary-700 mb-2">Subject</label>
                  <input
                    type="text"
                    {...register('subject')}
                    className="input-field text-sm"
                    placeholder="How can we help you?"
                  />
                  {errors.subject && <p className="text-primary-500 text-xs sm:text-sm mt-1">{errors.subject.message}</p>}
                </div>
              </div>

              <div>
                <label className="block text-xs sm:text-sm font-semibold text-secondary-700 mb-2">Message</label>
                <textarea
                  {...register('message')}
                  rows="4"
                  className="input-field resize-none text-sm"
                  placeholder="Tell us about your requirements..."
                />
                {errors.message && <p className="text-primary-500 text-xs sm:text-sm mt-1">{errors.message.message}</p>}
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="btn-primary w-full flex justify-center items-center gap-2 text-sm sm:text-base px-6 sm:px-8 py-3"
              >
                {isSubmitting ? (
                  <div className="animate-spin rounded-full h-5 w-5 sm:h-6 sm:w-6 border-b-2 border-white"></div>
                ) : (
                  <>Send Message <FaPaperPlane className="text-xs sm:text-sm" /></>
                )}
              </button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  )
}

export default ContactSection
